import { z } from "zod";

export const OFFICIAL_JURISDICTIONS = ["uk_hansard", "bundestag"] as const;
export const officialJurisdictionSchema = z.enum(OFFICIAL_JURISDICTIONS);
export type OfficialJurisdiction = z.infer<typeof officialJurisdictionSchema>;

export const officialParagraphSchema = z.object({
  anchor: z.string().min(1),
  text: z.string().min(1),
  column: z.string().nullable(),
  page: z.number().int().positive().nullable(),
});

export const officialSpeakerSchema = z.object({
  id: z.string().nullable(),
  label: z.string().min(1),
  name: z.string().nullable(),
  party: z.string().nullable(),
  role: z.string().nullable(),
});
export type OfficialSpeaker = z.infer<typeof officialSpeakerSchema>;

export const officialContributionSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(["speech", "interjection", "procedural"]),
  speaker: officialSpeakerSchema.nullable(),
  paragraphs: z.array(officialParagraphSchema).min(1),
  timecode: z.string().nullable(),
});
export type OfficialContribution = z.infer<typeof officialContributionSchema>;

export const officialSectionSchema = z.object({
  id: z.string().min(1),
  path: z.array(z.string()),
  title: z.string(),
  contributions: z.array(officialContributionSchema),
});
export type OfficialSection = z.infer<typeof officialSectionSchema>;

export const officialWrittenSchema = z.object({
  form: z.enum(["written_answer", "written_statement"]),
  house: z.string(),
  department: z.string().nullable(),
  reference: z.string().nullable(),
  tabled: z.string().nullable(),
  asked_by: z.object({ id: z.string().nullable(), name: z.string().nullable() }).nullable(),
  holding: z.boolean(),
  corrected: z.boolean(),
});
export type OfficialWritten = z.infer<typeof officialWrittenSchema>;

export const officialRecordSchema = z.object({
  schema: z.literal("amber.official-record/1"),
  jurisdiction: officialJurisdictionSchema,
  adapter: z.string(),
  observed_at: z.string(),
  record_id: z.string().min(1),
  title: z.string(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  chamber: z.string(),
  session: z.string().nullable(),
  language: z.enum(["en", "de"]),
  publication: z.object({ name: z.string(), document_id: z.string(), url: z.string(), volume: z.string().nullable() }),
  focus: z.string().nullable(),
  written: officialWrittenSchema.optional(),
  sections: z.array(officialSectionSchema).min(1),
  links: z.array(
    z.object({ kind: z.enum(["previous", "next", "media", "parent"]), url: z.string(), title: z.string().nullable() }),
  ),
});
export type OfficialRecord = z.infer<typeof officialRecordSchema>;

export const OFFICIAL_SOURCE_LABELS: Record<OfficialJurisdiction, string> = {
  uk_hansard: "Hansard Official Report",
  bundestag: "Plenarprotokoll des Deutschen Bundestages",
};

export function focusSections(record: OfficialRecord): OfficialSection[] {
  if (record.focus === null) return record.sections;
  return record.sections.filter((s) => s.id === record.focus);
}
